import type { Config } from '@schibsted-svp/web-player';
import { getConsentsForPulse } from '@vgtv/consent-manager';

import type { AdnContext } from '../types/global';
import { getUtmParams } from '../utils/getUtmParams';
import { getAssetAspectRatio } from '../utils/player/asset';
import { getIsFloatingFromPlayerConfig } from '../utils/player/dom';

import { getPortalHostname } from './helpers/getPortalHostname';
import type { StreamProvider } from '@vgtv/api-client';

const utmParameterToOriginKey = {
  utm_source: 'source',
  utm_medium: 'channel',
  utm_channel: 'channel',
  utm_campaign: 'campaign',
  utm_term: 'terms',
  utm_content: 'content',
} as const;

type OriginKey =
  (typeof utmParameterToOriginKey)[keyof typeof utmParameterToOriginKey];

const getOriginFromUtmParameters = ():
  Partial<Record<OriginKey, string>> | undefined => {
  const params = getUtmParams() as Record<string, string>;
  const origin: Partial<Record<OriginKey, string>> = {};

  (Object.keys(params) as (keyof typeof utmParameterToOriginKey)[]).forEach(
    (key) => {
      const mapped = utmParameterToOriginKey[key];
      if (mapped && params[key]) {
        origin[mapped] = params[key];
      }
    }
  );

  return Object.keys(origin).length > 0 ? origin : undefined;
};

export type PulseDecorator = Exclude<
  Exclude<Config['pulse'], undefined>['decorator'],
  undefined
>;

export const createPulseDecorator =
  (
    provider: StreamProvider,
    experiments: PulseExperiment[],
    adnContext: AdnContext,
    originalDecorator?: PulseDecorator
  ): PulseDecorator =>
  (eventData, assetModel) => {
    if (!eventData.experiments) eventData.experiments = [];
    experiments.forEach((e) => {
      (eventData.experiments as PulseExperiment[]).push(e);
    });

    const object = eventData.object as Record<string, unknown>;

    // overwrite the object.embed field:
    // embed = true for everything except for SViP hostname (excluding /embed)
    object.embed = !(
      window.location.hostname === getPortalHostname(provider) &&
      !/\/embed\/?/.test(window.location.pathname)
    );

    const { pcFormat } = adnContext.adnPlugin ?? {};
    if (pcFormat) {
      // To satisfy both schema and documentation
      eventData.pcFormat = pcFormat;
      object.pcFormat = pcFormat;
    }

    const custom = (object['spt:custom'] || {}) as Record<
      string,
      string | number | boolean
    >;
    object['spt:custom'] = custom;

    const { rawAsset: asset } = assetModel;
    custom['spt:assetAspectRatio'] = getAssetAspectRatio(asset);
    if (asset.series) {
      custom['spt:assetEpisodeNumber'] = asset.series.episodeNumber;
      custom['spt:assetSeasonNumber'] = asset.series.seasonNumber;
    }

    if (eventData.intent === 'Stop') {
      const data = eventData as {
        position: number;
        object: { duration: number };
      };
      custom['spt:completionRate'] = Math.round(
        (data.position * 100) / data.object.duration
      );
    }

    eventData.consents = getConsentsForPulse();

    const { player } = adnContext;
    object.sticky = player?.getFloating();

    const utmOrigin = getOriginFromUtmParameters();
    if (utmOrigin) {
      eventData.origin = {
        ...(eventData.origin ?? {}),
        ...utmOrigin,
      };
    }

    if (originalDecorator) {
      return originalDecorator(eventData, assetModel);
    }
    return eventData;
  };

export type PulseExperiment = {
  id: string;
  name: string;
  variant: string;
  payload?: unknown;
};
